import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import {
  PageHeader, StatCard, Card, CardTitle, Badge, DataTable, Td, Tr,
  SectionHeader, FilterBar, SearchBar, FilterSelect, Paginator, ResultCount, Btn,
} from '../components/UI';
import { ReportGenerator } from '../components/ReportGenerator';

const CYLINDERS = ['14.2 kg (Regular)','5 kg (Small)','19 kg (Commercial)','47.5 kg (Commercial Large)'];

function BarChart({ data, colorFn }) {
  const max = Math.max(...data.map(d=>d.value), 1);
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
      {data.map(d => (
        <div key={d.label}>
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:6, alignItems:'center' }}>
            <span style={{ color:'var(--text-secondary)', fontWeight:500 }}>{d.label}</span>
            <span style={{ fontWeight:700, color:'var(--text-primary)', fontFamily:'var(--font-mono)', fontSize:12.5 }}>
              {d.value}
              <span style={{ color:'var(--text-muted)', fontWeight:400 }}> ({Math.round(d.value/max*100)}%)</span>
            </span>
          </div>
          <div style={{ height:8, background:'var(--bg-elevated)', borderRadius:99, overflow:'hidden' }}>
            <div style={{
              height:'100%', borderRadius:99,
              background: colorFn ? colorFn(d.label) : 'var(--brand)',
              width:`${Math.max(Math.round(d.value/max*100), d.value>0?2:0)}%`,
              transition:'width .7s cubic-bezier(.4,0,.2,1)',
            }} />
          </div>
        </div>
      ))}
    </div>
  );
}

const STATUS_COLORS = {
  Delivered:'var(--accent-green)', Pending:'var(--accent-amber)',
  'Out for Delivery':'var(--accent-blue)', Cancelled:'var(--accent-red)',
};

export default function Reports() {
  const [stats, setStats] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('analytics');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);

  useEffect(() => {
    Promise.all([api.getStats(), api.getCustomers({limit:200}), api.getBookings({limit:500})])
      .then(([s,c,b]) => {
        setStats(s.data);
        setCustomers(c.data.customers || []);
        setBookings(b.data.bookings || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const delivered = bookings.filter(b => b.status==='Delivered');
  const deliveryRate = bookings.length ? Math.round(delivered.length / bookings.length * 100) : 0;
  const cylData = CYLINDERS.map(ct => ({ label:ct, value:customers.filter(c=>c.cylinderType===ct).length }));
  const statusData = ['Delivered','Pending','Out for Delivery','Cancelled'].map(s => ({
    label:s, value:bookings.filter(b=>b.status===s).length,
  }));

  // Customer table with search + pagination
  const filtered = customers.filter(c =>
    !search || c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.connectionNo?.toLowerCase().includes(search.toLowerCase())
  );
  const paged = filtered.slice((page-1)*perPage, page*perPage);
  const totalPages = Math.ceil(filtered.length / perPage);

  const tabs = [
    { key:'analytics', label:'Analytics', icon:'◉' },
    { key:'customer-report', label:'Customer Report', icon:'◎' },
    { key:'generate', label:'Generate / Export', icon:'⊠' },
  ];

  return (
    <div>
      <PageHeader title="Reports & Analytics" subtitle="System-wide statistics and performance metrics" />

      {/* Tabs */}
      <div style={{ display:'flex', gap:4, marginBottom:22, borderBottom:'1px solid var(--border-subtle)', paddingBottom:0 }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
            display:'flex', alignItems:'center', gap:6,
            padding:'9px 16px', background:'transparent', border:'none',
            borderBottom: activeTab===t.key ? '2px solid var(--brand)' : '2px solid transparent',
            color: activeTab===t.key ? 'var(--brand)' : 'var(--text-secondary)',
            fontSize:13, fontWeight:600, cursor:'pointer', transition:'var(--transition)',
            marginBottom:-1,
          }}>
            <span style={{ fontSize:14 }}>{t.icon}</span>
            {t.label}
          </button>
        ))}
      </div>

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div>
          {/* Stat cards */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:14, marginBottom:22 }}>
            <StatCard label="Total Revenue" value={`₹${(stats?.totalRevenue||0).toLocaleString('en-IN')}`} icon="💰" color="var(--accent-green)" />
            <StatCard label="Active Customers" value={stats?.activeCustomers||0} icon="👥" color="var(--accent-blue)" />
            <StatCard label="Delivery Rate" value={`${deliveryRate}%`} icon="📦" color={deliveryRate>80?'var(--accent-green)':'var(--accent-amber)'} />
            <StatCard label="Avg Bookings" value={customers.length ? (bookings.length/customers.length).toFixed(1) : 0} icon="📊" color="var(--accent-purple)" />
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))', gap:18, marginBottom:18 }}>
            <Card className="au d2">
              <CardTitle>Cylinder Type Distribution</CardTitle>
              {loading ? <div style={{ height:120, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--text-muted)' }}>Loading…</div>
                : <BarChart data={cylData} colorFn={() => 'var(--brand)'} />}
            </Card>
            <Card className="au d3">
              <CardTitle>Booking Status Breakdown</CardTitle>
              {loading ? <div style={{ height:120, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--text-muted)' }}>Loading…</div>
                : <BarChart data={statusData} colorFn={l => STATUS_COLORS[l]||'var(--text-muted)'} />}
            </Card>
          </div>

          {/* Revenue by month summary */}
          <Card className="au d4">
            <CardTitle>Quick Summary</CardTitle>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))', gap:16 }}>
              {[
                { label:'Total Bookings', value:bookings.length, icon:'📋' },
                { label:'Delivered', value:delivered.length, icon:'✅' },
                { label:'Pending', value:bookings.filter(b=>b.status==='Pending').length, icon:'⏳' },
                { label:'Cancelled', value:bookings.filter(b=>b.status==='Cancelled').length, icon:'❌' },
                { label:'Total Customers', value:customers.length, icon:'🧑‍🤝‍🧑' },
                { label:'Active Customers', value:customers.filter(c=>c.status==='Active').length, icon:'✔' },
              ].map(s => (
                <div key={s.label} style={{ textAlign:'center', padding:'14px 10px', background:'var(--bg-elevated)', borderRadius:'var(--radius-md)', border:'1px solid var(--border-subtle)' }}>
                  <div style={{ fontSize:24, marginBottom:6 }}>{s.icon}</div>
                  <div style={{ fontSize:22, fontWeight:800, color:'var(--text-primary)', letterSpacing:'-0.02em' }}>{s.value}</div>
                  <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:4, fontWeight:500 }}>{s.label}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* Customer Report Tab */}
      {activeTab === 'customer-report' && (
        <div>
          <FilterBar actions={<ResultCount count={filtered.length} total={customers.length} label="customer" />}>
            <SearchBar value={search} onChange={v => { setSearch(v); setPage(1); }} placeholder="Search customer, connection..." />
          </FilterBar>

          <Card noPad className="au d1">
            <DataTable
              loading={loading}
              empty={filtered.length===0 ? 'No customers found' : null}
              headers={['Customer','Connection','Cylinder','Bookings','Spent','Last Booking','Days Since','Status']}
            >
              {paged.map(c => {
                const cbs = bookings.filter(b => b.customerId===c.id && b.status==='Delivered');
                const lb = cbs.sort((a,b) => new Date(b.bookingDate)-new Date(a.bookingDate))[0];
                const days = lb ? Math.floor((Date.now()-new Date(lb.bookingDate))/86400000) : null;
                return (
                  <Tr key={c.id}>
                    <Td>
                      <div style={{ fontWeight:600, fontSize:13 }}>{c.name}</div>
                      <div style={{ fontSize:11, color:'var(--text-muted)' }}>{c.phone}</div>
                    </Td>
                    <Td><span style={{ fontFamily:'var(--font-mono)', fontSize:12, color:'var(--text-secondary)' }}>{c.connectionNo}</span></Td>
                    <Td><Badge type="gray">{c.cylinderType?.split(' ').slice(0,2).join(' ')}</Badge></Td>
                    <Td style={{ fontWeight:700, fontFamily:'var(--font-mono)' }}>{cbs.length}</Td>
                    <Td style={{ fontWeight:700, color:'var(--accent-green)', fontFamily:'var(--font-mono)', fontSize:12.5 }}>
                      ₹{cbs.reduce((s,b)=>s+parseFloat(b.amount||0),0).toLocaleString('en-IN')}
                    </Td>
                    <Td style={{ fontSize:12, color:'var(--text-secondary)' }}>{lb ? lb.bookingDate : '—'}</Td>
                    <Td>
                      {days !== null
                        ? <Badge type={days>=30?'warning':days>=45?'danger':'success'}>{days}d</Badge>
                        : <Badge type="gray">N/A</Badge>}
                    </Td>
                    <Td><Badge type={c.status==='Active'?'success':'danger'} dot>{c.status}</Badge></Td>
                  </Tr>
                );
              })}
            </DataTable>
            <Paginator
              page={page} totalPages={totalPages}
              totalItems={filtered.length} perPage={perPage}
              onPage={setPage} onPerPage={p => { setPerPage(p); setPage(1); }}
            />
          </Card>
        </div>
      )}

      {/* Generate/Export Tab */}
      {activeTab === 'generate' && <ReportGenerator bookings={bookings} customers={customers} />}
    </div>
  );
}
